// Detects whether the app is currently running inside the Android app (Trusted Web Activity
// wrapping this same PWA, published on Google Play) versus a normal desktop/mobile browser tab.
// This drives two things across the app:
//  1. Payment method: Google Play Billing only inside the TWA, Mollie only in the browser
//     (Google Play policy forbids alternative payment processors for digital goods inside a
//     Play-distributed app - see usePlayBillingPurchase.js).
//  2. UI chrome: no footer / no external-navigating links inside the TWA (Google Play review
//     expects an app-like experience, not "a website with a link list").
//
// Detection signals (see https://web.dev/learn/pwa/detection):
//  - document.referrer starting with "android-app://" is the strongest signal: it means this
//    exact page load was launched from an installed Android app (the TWA wrapper).
//  - window.matchMedia('(display-mode: standalone)') catches the case where the PWA was
//    "Added to Home Screen" directly from the browser (not the Play Store app) - we still
//    treat this as "standalone", not "twa", since Play Billing isn't available there.
// Sticky TWA flag (sessionStorage = isolé par onglet, donc jamais partagé avec le Chrome
// "navigateur" du même téléphone) : document.referrer n'est présent QUE sur la toute première
// navigation lancée par le wrapper Android. Après un reload, une redirection de paiement ou une
// restauration de l'app, il est vide et l'app était alors traitée comme web -> les prix
// s'affichaient dans l'app Android (interdit, Google Play gère les prix). Une fois "twa"
// détecté, on le mémorise pour toute la session.
const TWA_SESSION_FLAG = "kalypso_launch_twa";

export function rememberTwaContext() {
  try { window.sessionStorage.setItem(TWA_SESSION_FLAG, "1"); } catch { /* storage bloqué */ }
}

export function getLaunchContext() {
  if (typeof window === "undefined" || typeof document === "undefined") return "unknown";
  try {
    if (document.referrer && document.referrer.startsWith("android-app://")) {
      rememberTwaContext();
      return "twa";
    }
    try {
      if (window.sessionStorage.getItem(TWA_SESSION_FLAG) === "1") return "twa";
    } catch { /* storage bloqué */ }
    if (window.matchMedia("(display-mode: standalone)").matches) return "standalone";
    if (window.matchMedia("(display-mode: fullscreen)").matches) return "fullscreen";
    if (window.matchMedia("(display-mode: minimal-ui)").matches) return "minimal-ui";
    return "browser";
  } catch {
    return "unknown";
  }
}

export function isAndroidApp() {
  return getLaunchContext() === "twa";
}

// The `document.referrer` signal above is a good *general UI* indicator (hide the footer,
// mailto links, etc. - low stakes if occasionally imprecise) but Google explicitly recommends
// NOT relying on it to decide whether Google Play Billing is actually usable right now: the
// authoritative signal is whether the Digital Goods API itself resolves successfully, since
// that's the exact same check Chrome performs before allowing a `https://play.google.com/billing`
// PaymentRequest to go through. Referrer-based detection can occasionally disagree with this
// (e.g. across different TWA/Chrome builds) even while the app is correctly running full-screen,
// which is exactly what caused Google Play Billing to silently fall back to the web/Mollie flow
// for some users - see useTokenPurchase.js, the only place this must be 100% correct.
// Memoized per page load (14/08/2026, "order_939b7c6c609f" incident: a customer on a real
// Android 14 phone tapped a token pack, got repeated PostHog "dead clicks" and never paid -
// zero backend trace at all, meaning the click handler was stuck BEFORE ever reaching the
// Play Billing sheet or the Mollie card form). Root cause: every single tap re-ran this whole
// probe from scratch, each one able to hang up to 6s with ZERO visual feedback in the
// meantime (no spinner, no disabled state) - so every tap during that window looked, and was
// tracked as, a dead click. Caching the result the first time it's computed means only the
// very first check on a page can ever be slow; every later click reuses the settled promise
// instantly. See Checkout.jsx/TokenPackPicker.jsx for the matching "instant loading state on
// tap" half of this fix.
let googlePlayBillingPromise = null;
export function resetGooglePlayBillingCache() {
  googlePlayBillingPromise = null;
}
export async function canUseGooglePlayBilling() {
  if (typeof window === "undefined" || !("getDigitalGoodsService" in window)) return false;
  if (!googlePlayBillingPromise) {
    googlePlayBillingPromise = (async () => {
      try {
        // Same hang risk as request.show() in useTokenPurchase.js (13/08/2026, real production
        // session: a Huawei phone on Android 6 inside the actual Play-distributed app, where this
        // call never resolved NOR rejected) - a broken/missing Play Billing service binding on the
        // device leaves this promise pending forever. Without a timeout, EVERY caller of this
        // function (both the "buy pack" click handler and Checkout.jsx's auto pack-selection
        // effect) would hang too, leaving the visitor with either silent "dead clicks" or a
        // payment form that never appears at all - worse than the (rare) policy edge case of
        // falling back to the web flow on a device that turns out to be Play-Billing-incapable.
        const timeout = new Promise((_, reject) => setTimeout(() => reject(new Error("timeout")), 6000));
        await Promise.race([window.getDigitalGoodsService("https://play.google.com/billing"), timeout]);
        return true;
      } catch {
        return false;
      }
    })();
  }
  return googlePlayBillingPromise;
}
